/**
 * D-18 — polarity, PORTED from `cloud/dp/shell/src/platform/usePolarity.ts`.
 *
 * ⛔ A port, not a reuse from CP, because CP HAS NO POLARITY MECHANISM AT ALL:
 * `grep -rn "usePolarity\|PolarityToggle" cloud/cp` returns zero hits, and
 * `site-admin-ui/index.html` hardcodes `class="dark"` on `<html>` (verified on HEAD:
 * `<html lang="en" class="dark">`). Spine §8.2 records that the design doc claims
 * otherwise. This shell deliberately does NOT inherit that hardcode — the class is owned
 * here, so the toggle WS-E adds does not read as broken on first paint.
 *
 * ⚠️ SCOPE. This module is the pure resolve-and-apply core plus the pre-paint entry point
 * that `src/main.tsx` calls (WS-A §2.7 specifies that call verbatim, so this file has to
 * exist for the package to compile). The React surface — `usePolarity`, `PolarityContext`,
 * `PolarityToggle` — is WS-E's, and is deliberately NOT here. WS-E should import
 * `POLARITY_STORAGE_KEY`, `readStoredPolarity`, `writeStoredPolarity`, `resolvePolarity`
 * and `applyPolarityToDom` rather than re-deriving any of them, so there is exactly one
 * definition of "which class goes on `<html>`".
 *
 * ⚠️ One deliberate DIVERGENCE from the DP original, and it is a fix rather than a
 * transcription slip: DP holds `const mql = window.matchMedia(...)` at MODULE scope and
 * calls `applyToDOM(...)` as a module-load side effect. Both run at import time, so the
 * module throws in any environment without a DOM and cannot be imported by a test that
 * wants to control the system preference. Here the media query is read through a function,
 * and application is an explicit call. Same behaviour in the browser; testable everywhere.
 */
import type { ResolvedPolarity, ShellPolarity } from '@zion/sdk-contracts';

/**
 * ⛔ This shell's OWN key, namespaced like every other piece of its persisted state
 * (D-6′ / D-9′). `localStorage` is ORIGIN-scoped and both consoles live on
 * https://admin.zion.witness.ai, so a shared key means two writers to one slot. Polarity
 * is not rotation-sensitive the way the refresh token is, so the failure would be milder
 * — one console silently restyling the other — but the rule is the same rule and there is
 * no reason to make an exception for the harmless case.
 */
export const POLARITY_STORAGE_KEY = 'zion.cpadmin.polarity';

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * The persisted preference, or `'system'`.
 *
 * `try/catch` because `localStorage` throws outright in some private-browsing modes —
 * reading it is not the safe operation it looks like. An unrecognised stored value also
 * degrades to `'system'`: the alternative is trusting a string we did not write.
 */
export function readStoredPolarity(): ShellPolarity {
  try {
    const stored = localStorage.getItem(POLARITY_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Unreadable storage is indistinguishable from unset, and both mean "follow the OS".
  }
  return 'system';
}

/** Persist the preference. A failed write is non-fatal — the choice holds for this tab. */
export function writeStoredPolarity(polarity: ShellPolarity): void {
  try {
    localStorage.setItem(POLARITY_STORAGE_KEY, polarity);
  } catch {
    // Non-fatal by design: the in-memory state is already correct, so the user's click
    // took effect. Only its persistence across a reload is lost.
  }
}

/**
 * Whether the OS currently prefers dark. `false` when `matchMedia` is unavailable.
 *
 * ⚠️ There is deliberately NO `typeof window === 'undefined'` guard. This is a browser-only SPA
 * — `applyPolarityToDom` below touches `document` unconditionally, and so does the branding layer
 * — so such a guard would be UNREACHABLE, and an unreachable branch cannot be covered without
 * either a coverage suppression (banned) or a test that lies about the environment. The
 * `matchMedia` capability check IS reachable (delete the property) and is the one that earns its
 * keep: the honest default for "cannot tell" is the light baseline the `@theme` block defines,
 * not an assumption.
 */
export function systemPrefersDark(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(SYSTEM_DARK_QUERY).matches;
}

/** Collapse a preference plus the OS state into the one of two values the DOM can carry. */
export function resolvePolarity(
  preference: ShellPolarity,
  systemIsDark: boolean,
): ResolvedPolarity {
  if (preference === 'system') return systemIsDark ? 'dark' : 'light';
  return preference;
}

/**
 * Add or remove `.dark` on `<html>`, which is what the `.dark { … }` block in index.css
 * keys off. ⛔ `classList.add`/`remove`, never `className =` — the branding layer and
 * Tailwind both put other classes on this element and a wholesale assignment would drop
 * them.
 */
export function applyPolarityToDom(resolved: ResolvedPolarity): void {
  const element = document.documentElement;
  if (resolved === 'dark') element.classList.add('dark');
  else element.classList.remove('dark');
}

/**
 * Resolve the persisted polarity and put it on `<html>` BEFORE first paint.
 *
 * Called first in `src/main.tsx`, ahead of the async branding fetch, and the order is
 * load-bearing: applying the theme after a network round-trip flashes the wrong polarity
 * for its whole duration.
 */
export function applyPolarityBeforePaint(): void {
  applyPolarityToDom(resolvePolarity(readStoredPolarity(), systemPrefersDark()));
}
